import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PolynomialCalculator = () => {
  const [polynomial1, setPolynomial1] = useState('');
  const [polynomial2, setPolynomial2] = useState('');
  const [operation, setOperation] = useState('add');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e, polynomial) => {
    const value = e.target.value;
    if (polynomial === 1) {
      setPolynomial1(value);
    } else if (polynomial === 2) {
      setPolynomial2(value);
    }
    setError('');
  };

  const handleOperationChange = (e) => {
    setOperation(e.target.value);
  };

  const calculatePolynomial = () => {
    if (!polynomial1 || !polynomial2) {
      setError('Please enter both polynomials');
      return;
    }

    try {
      let result;
      switch (operation) {
        case 'add':
          result = addPolynomials(polynomial1, polynomial2);
          break;
        case 'subtract':
          result = subtractPolynomials(polynomial1, polynomial2);
          break;
        case 'multiply':
          result = multiplyPolynomials(polynomial1, polynomial2);
          break;
        case 'divide':
          result = dividePolynomials(polynomial1, polynomial2);
          break;
        default:
          setError('Please select a valid operation');
          return;
      }

      setResult(result);
    } catch (error) {
      setError('An error occurred while calculating the polynomials. Please check your input.');
    }
  };

  // Dummy functions for polynomial operations (to be replaced with actual logic)
  const addPolynomials = (p1, p2) => `Result of adding ${p1} and ${p2}`;
  const subtractPolynomials = (p1, p2) => `Result of subtracting ${p2} from ${p1}`;
  const multiplyPolynomials = (p1, p2) => `Result of multiplying ${p1} and ${p2}`;
  const dividePolynomials = (p1, p2) => `Result of dividing ${p1} by ${p2}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Polynomial Calculator</title>
          <meta name="description" content="Perform operations on polynomials such as addition, subtraction, multiplication, and division." />
          <meta name="keywords" content="polynomial calculator, add polynomials, subtract polynomials, multiply polynomials, divide polynomials" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">POLY</div>
            <h1 className="text-3xl font-bold text-gray-800">Polynomial Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Perform operations on polynomials such as addition, subtraction, multiplication, and division.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Polynomial 1:</label>
              <input
                type="text"
                value={polynomial1}
                onChange={(e) => handleInputChange(e, 1)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter polynomial 1 (e.g., 2x^2 + 3x + 1)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Polynomial 2:</label>
              <input
                type="text"
                value={polynomial2}
                onChange={(e) => handleInputChange(e, 2)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter polynomial 2 (e.g., x^2 - x + 4)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Operation:</label>
              <select
                value={operation}
                onChange={handleOperationChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="add">Addition</option>
                <option value="subtract">Subtraction</option>
                <option value="multiply">Multiplication</option>
                <option value="divide">Division</option>
              </select>
            </div>
          </div>

          <button
            onClick={calculatePolynomial}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Polynomials
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Polynomial Result</h3>
                <p className="text-lg">{result}</p>
              </div>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Polynomial Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Polynomials:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the coefficients of the first polynomial (e.g., 2x^2 + 3x + 1).</li>
                  <li>Input the coefficients of the second polynomial (e.g., x^2 - x + 4).</li>
                </ul>
              </li>
              <li><strong>Select Operation:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the operation you want to perform (addition, subtraction, multiplication, or division).</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The result of the selected operation on the two polynomials.</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated polynomial result.</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure the polynomials are in standard form (e.g., ax^n + bx^(n-1) + ... + c).</li>
                <li>Use this calculator for algebra problems, engineering calculations, or any scenario involving polynomial operations.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for adding two polynomials:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Polynomial 1: 2x^2 + 3x + 1</li>
                <li>Polynomial 2: x^2 - x + 4</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Add the coefficients of the same degree terms
                  <ul className="list-disc pl-6">
                    <li>Result: (2x^2 + x^2) + (3x - x) + (1 + 4) = 3x^2 + 2x + 5</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p>Here are some frequently asked questions about the Polynomial Calculator:</p>
            <div className="mt-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What is a polynomial?</strong>
                <p>A polynomial is an expression consisting of variables and coefficients, involving operations of addition, subtraction, multiplication, and non-negative integer exponents of variables.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: How do I enter polynomials?</strong>
                <p>Enter polynomials in standard form, such as 2x^2 + 3x + 1. Use '^' to denote exponents.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What operations can I perform?</strong>
                <p>You can perform addition, subtraction, multiplication, and division of polynomials.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What if the polynomials have different degrees?</strong>
                <p>The calculator will handle polynomials of different degrees. It will align the terms based on their degrees and perform the operation accordingly.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PolynomialCalculator;